// Replace your existing PreviewSTTemplate with this (keeps your original imports)
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { useHistory, useParams } from "react-router-dom";
import makeAnimated from "react-select/animated";
import { Button } from "../../elements/Button";
import commoncss from "../../css/common.module.css";
import buttoncss from "../../css/button.module.css";
import Breadcrumb from "../../components/Breadcrumb";
import PreviewSidebar from "../../components/templateLibrary/PreviewSidebar";
import PreviewTemplateTableGenerator from "./PreviewTemplateTableGenerator";
import RenderBSPLTables from "../../components/templateLibrary/RenderBSPLTables";
import QuillReactEditor from "../../components/QuillReactEditor";
import libcss from "../../css/library.module.css";
import { getTemplateDetailsApiHandler } from "../../../WorkSpace/Components/ApiServices/apiHandler";
import { ReactComponent as CloseIcon } from "../../assets/icons/Close-green.svg";
const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: genId(),
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block.textRaw,
        textPlain: block.textPlain,
        text: [
          {
            id: genId(),
            content: block?.textRaw ?? "",
          },
        ],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: genId(),
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow)
          ? block.tableRow.map((r) => ({ ...r }))
          : [],
        tableColumn: Array.isArray(block.tableColumn)
          ? block.tableColumn.map((c) => ({ ...c }))
          : [],
      });
    }
  });
  return items;
};

const PreviewSTTemplate = () => {
  const animatedComponents = makeAnimated();
  let history = useHistory();
  const routerHistory = useHistory();
  const dispatch = useDispatch();
  //const [libraryName] = useState("ST Library");

  const [templateData, setTemplateData] = useState(null);
  const [selCat, setSelCat] = useState(null);
  const { tempId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState(() => normalizeFromInitial(null));

  const [openSubmenu, setOpenSubmenu] = useState(0);
  const [activeSubmenu, setActiveSubmenu] = useState({
    menuIndex: 0,
    subIndex: 0,
    subSubIndex: null,
    clickId: null,
  });

  const [targetSubGroupId, setTargetSubGroupId] = useState(null);
  const scrollContainerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    getTemplateDetails();
  }, []);

  const getTemplateDetails = () => {
    setIsLoading(true);
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          let td = response?.data?.groupingTemplate;
          if (td) {
            let noteCounter = 1;
            ['generalNotes', 'BSNotes', 'PLNotes'].forEach(key => {
              if (Array.isArray(td[key])) {
                td[key].forEach(note => {
                  note.notes = noteCounter++;
                });
              }
            });
          }
          setTemplateData(td);
        }
      })
      .catch((error) => {
        // handle error if needed
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // now showData accepts optional subIdx for second-level subNotes
  const showData = (note, idx, subIdx = null) => {
    setSelCat(note);
    if (note === "BS" || note === "PL") {
      // BS / PL handled elsewhere (tables)
      setItems(normalizeFromInitial(null));
      return;
    }

    const arr = templateData?.[note] || [];
    const target = typeof idx === "number" && arr[idx] ? arr[idx] : null;

    if (!target) {
      // fallback: empty
      setItems(normalizeFromInitial(null));
      return;
    }

    // if subIdx provided and the target has subNotes, show subNote's data
    if (
      subIdx !== null &&
      Array.isArray(target.subNotes) &&
      target.subNotes[subIdx]
    ) {
      setItems(normalizeFromInitial(target.subNotes[subIdx]));
    } else {
      // show top-level item's data
      setItems(normalizeFromInitial(target));
    }
  };

  const menuItems = [
    { label: "Balance Sheet", value: "BS" },
    { label: "Profit & Loss", value: "PL" },
    { label: "SOCIE", value: "socie" },
    { label: "Cashflow", value: "cashflow" },
    { label: "General Notes", value: "generalNotes" },
    { label: "Balance Sheet Notes", value: "BSNotes" },
    { label: "Profit & Loss Statement Notes", value: "PLNotes" },
    { label: "Disclosures", value: "disclosure" },
  ];

  const [redirectNote, setRedirectNote] = useState(null);

  const handleNoteClick = (noteObj, category) => {
    setIsScrolling(true);
    const payload = { ...noteObj, sourceCategory: category };
    setRedirectNote(payload);
    if (payload.l2Name) {
      setTargetSubGroupId(payload.l2Name);
    }
  };

  useEffect(() => {
    if (targetSubGroupId && templateData) {
      const timer = setTimeout(() => {
        const normalize = (str) => String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
        const normId = normalize(targetSubGroupId);
        
        // Try to find row or table by normalized ID
        let element = document.getElementById(normId);
        
        // Fallback: try to find by exact name if normalized fails
        if (!element) {
          element = document.getElementById(targetSubGroupId);
        }

        if (element && scrollContainerRef.current) {
          isProgrammaticScrollRef.current = true;
          
          const container = scrollContainerRef.current;
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          
          // Align block: "start" (with some top padding, e.g. 20px)
          const targetScrollTop = (elementRect.top - containerRect.top) + container.scrollTop - 20;
          
          container.scrollTo({
            top: targetScrollTop,
            behavior: "smooth",
          });

          element.classList.add(commoncss.noteSectionHighlight);
          setTimeout(() => {
            element.classList.remove(commoncss.noteSectionHighlight);
            isProgrammaticScrollRef.current = false;
          }, 2000);
          setTargetSubGroupId(null);
        } else {
          setIsScrolling(false);
        }
      }, 800); // Increased timeout to ensure DOM is fully ready
      return () => clearTimeout(timer);
    }
  }, [targetSubGroupId, templateData, selCat]);

  // We want to clear isScrolling after a short delay to account for the smooth scroll animation
  useEffect(() => {
    if (isScrolling && !targetSubGroupId) {
      const t = setTimeout(() => setIsScrolling(false), 500);
      return () => clearTimeout(t);
    }
  }, [isScrolling, targetSubGroupId]);

  // Smooth scroll to active section from sidebar click
  useEffect(() => {
    if (activeSubmenu.subIndex === null || !activeSubmenu.clickId) return;

    // Only set isScrolling if not triggered by redirect
    if (!redirectNote) {
      setIsScrolling(true);
    }

    // Set synchronously to block intersection observer immediately!
    isProgrammaticScrollRef.current = true;

    const timer = setTimeout(() => {
      let elementId = "";
      if (selCat === "BS" || selCat === "PL") {
        elementId = `table-${activeSubmenu.subIndex}`;
      } else {
        if (activeSubmenu.subSubIndex !== null) {
          elementId = `note-${activeSubmenu.subIndex}-${activeSubmenu.subSubIndex}`;
        } else {
          elementId = `note-${activeSubmenu.subIndex}`;
        }
      }

      const element = document.getElementById(elementId);
      if (element && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        
        // Align block: "start" (with some top padding, e.g. 20px)
        const targetScrollTop = (elementRect.top - containerRect.top) + container.scrollTop - 20;
        
        container.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });

        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 800);
      } else {
        // If element not found, we must release the lock!
        isProgrammaticScrollRef.current = false;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [activeSubmenu.clickId]);

  // Scroll Spy to highlight the active section on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observerOptions = {
      root: container,
      rootMargin: "-20% 0px -60% 0px", // Focus on elements near the top of the container
      threshold: 0,
    };

    const handleIntersection = (entries) => {
      if (isProgrammaticScrollRef.current) return;
      
      const visibleEntry = entries.find((entry) => entry.isIntersecting);
      if (!visibleEntry) return;

      const id = visibleEntry.target.id;
      if (id.startsWith("table-")) {
        const idx = parseInt(id.replace("table-", ""), 10);
        if (!isNaN(idx)) {
          setActiveSubmenu((prev) => {
            if (prev.subIndex === idx && prev.subSubIndex === null) return prev;
            return { ...prev, subIndex: idx, subSubIndex: null };
          });
        }
      } else if (id.startsWith("note-")) {
        const parts = id.replace("note-", "").split("-").map(Number);
        if (parts.length === 1 && !isNaN(parts[0])) {
          setActiveSubmenu((prev) => {
            if (prev.subIndex === parts[0] && prev.subSubIndex === null) return prev;
            return { ...prev, subIndex: parts[0], subSubIndex: null };
          });
        } else if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          setActiveSubmenu((prev) => {
            if (prev.subIndex === parts[0] && prev.subSubIndex === parts[1]) return prev;
            return { ...prev, subIndex: parts[0], subSubIndex: parts[1] };
          });
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    const updateObservations = () => {
      observer.disconnect();
      const targets = container.querySelectorAll('[id^="table-"], [id^="note-"]');
      targets.forEach((target) => observer.observe(target));
    };

    updateObservations();

    const mutationObserver = new MutationObserver(updateObservations);
    mutationObserver.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [selCat, templateData]);

  return (
    <>
      <div>
        <div className={`d-flex ${commoncss.pageContent}`}>
          {isScrolling && (
            <div style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              zIndex: 9999,
            }}>
              <style>{`@keyframes spin {0% {transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
              <div style={{
                display: "grid",
                placeItems: "center",
                background: "transparent",
                boxShadow: "none",
              }}>
                <div style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  border: "6px solid rgba(0, 0, 0, 0.1)",
                  borderTopColor: "#47b36b",
                  animation: "spin 0.9s linear infinite",
                }} />
              </div>
            </div>
          )}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className={commoncss.w100}
            style={{ maxHeight: "100vh", overflowY: "hidden" }}
          >      
            <div className={libcss.financialWrapper} style={{ maxWidth: "98%", width: "98%", margin: "10px auto", height: "calc(100vh - 20px)", display: "flex", flexDirection: "column" }}>
                <div className={libcss.previewHeader}>
                <h2 className={libcss.previewTitle}>
                  Preview – {templateData?.name || "Template Name"}
                </h2>
              <Button
                btnColor={buttoncss.secondary}
                label={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <CloseIcon width={30} height={30} />
                    <span>Exit Preview</span>
                  </div>
                }
                style={{
                  padding: "6px 12px",
                }}
                onClick={() => {
                  routerHistory.push(`/myfinV2/library/viewedit-library/${tempId}`);
                }}
              />
            </div>
            <div
              className={`${libcss.libBody} ${commoncss.dFlex}  ${commoncss.hideScroll} m-2`}
              style={{ width: "98%" }}
            >
              <div className={`${libcss.previewS1} ${commoncss.hideScroll} w-20`} style={{
                  height: "calc(100vh - 120px)",
                  overflowY: "auto",
                }}>
                <PreviewSidebar
                  menuItems={menuItems}
                  activeItem={0}
                  title={"Financial Statement"}
                  showData={showData}
                  templateData={templateData}
                  setSelCat={setSelCat}
                  redirectNote={redirectNote}
                  setRedirectNote={setRedirectNote}
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  openSubmenu={openSubmenu}
                  setOpenSubmenu={setOpenSubmenu}
                  isLoading={isLoading}
                  isScrolling={isScrolling}
                />
              </div>
              <div ref={scrollContainerRef} className={`${libcss.previewS2}  ${commoncss.hideScroll}`} style={{ height: "calc(100vh - 120px)", overflowY: "auto" }}>
                <div className={libcss.levelCatDiv} style={{ background: "#f8f9f9", padding: "20px 15px", minHeight: "100%" }}>
                  <div style={{ display: "grid", gap: 5 }}>
                    {/* Render all notes/items for categories other than BS/PL */}
                    {selCat !== "BS" && selCat !== "PL" && (
                      <div style={{ display: "grid", gap: 24 }}>
                        {(!templateData?.[selCat] || templateData[selCat].length === 0) && (
                          <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "50vh",
                            color: "#94a3b8",
                            fontSize: "16px",
                            fontWeight: "500",
                            fontFamily: "inherit"
                          }}>
                            No Data found
                          </div>
                        )}
                        {(templateData?.[selCat] || []).map((topItem, topIdx) => {
                          const topNormalized = normalizeFromInitial(topItem);
                          return (
                            <div 
                              key={topItem._id || topItem.id || `top-${topIdx}`} 
                              id={`note-${topIdx}`}
                              style={{ 
                                background: "#fff", 
                                borderRadius: "12px", 
                                border: "1px solid #e3efe7", 
                                padding: "20px",
                                scrollMarginTop: "20px"
                              }}
                            >
                              <h4 style={{ color: "#205a46", fontWeight: 600, fontSize: "16px", marginBottom: "16px" }}>
                                {topItem.name}
                              </h4>
                              
                              <div style={{ display: "grid", gap: 14 }}>
                                {topNormalized.map((it) => (
                                  <div key={it.id}>
                                    {it.type === "text" ? (
                                      <div style={{ marginTop: 10 }}>
                                        <QuillReactEditor value={it.text?.[0]?.content ?? ""} disabled={true} />
                                      </div>
                                    ) : it.type === "table" ? (
                                      <div style={{ marginTop: 10 }} className={commoncss.hideScroll}>
                                        <PreviewTemplateTableGenerator
                                          selCat={selCat}
                                          value={{
                                            tableTitle: it.tableTitle,
                                            tableRow: it.tableRow,
                                            tableColumn: it.tableColumn,
                                          }}
                                        />
                                      </div>
                                    ) : null}
                                  </div>
                                ))}
                              </div>

                              {/* Render Sub Notes */}
                              {Array.isArray(topItem.subNotes) && topItem.subNotes.map((sn, sIdx) => {
                                const snNormalized = normalizeFromInitial(sn);
                                return (
                                  <div 
                                    key={sn._id || sn.id || `sn-${topIdx}-${sIdx}`}
                                    id={`note-${topIdx}-${sIdx}`}
                                    style={{ 
                                      marginTop: "24px", 
                                      paddingLeft: "16px", 
                                      borderLeft: "3px solid #c2e6d1",
                                      scrollMarginTop: "20px"
                                    }}
                                  >
                                    <h5 style={{ color: "#33584e", fontWeight: 600, fontSize: "14px", marginBottom: "12px" }}>
                                      {sn.name}
                                    </h5>
                                    <div style={{ display: "grid", gap: 14 }}>
                                      {snNormalized.map((it) => (
                                        <div key={it.id}>
                                          {it.type === "text" ? (
                                            <div style={{ marginTop: 10 }}>
                                              <QuillReactEditor value={it.text?.[0]?.content ?? ""} disabled={true} />
                                            </div>
                                          ) : it.type === "table" ? (
                                            <div style={{ marginTop: 10 }} className={commoncss.hideScroll}>
                                              <PreviewTemplateTableGenerator
                                                selCat={selCat}
                                                value={{
                                                  tableTitle: it.tableTitle,
                                                  tableRow: it.tableRow,
                                                  tableColumn: it.tableColumn,
                                                }}
                                              />
                                            </div>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selCat === "BS" && (
                      <RenderBSPLTables
                        data={templateData?.grouping}
                        category={"BS"}
                        selectedIndex={activeSubmenu.subIndex}
                        templateData={templateData}
                        onNoteClick={handleNoteClick}
                      />
                    )}

                    {selCat === "PL" && (
                      <RenderBSPLTables
                        data={templateData?.grouping}
                        category={"PL"}
                        notesCount={templateData?.generalNotes?.length}
                        selectedIndex={activeSubmenu.subIndex}
                        templateData={templateData}
                        onNoteClick={handleNoteClick}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        </div>
      </div>
    </>
  );
};

export default PreviewSTTemplate;
